import { useState, useEffect, useCallback } from "react";
import api from "@/utils/api";
import { Plus, Trash2, RefreshCw, Cpu, Wifi } from "lucide-react";
import DeviceOnboardWizard from "@/components/DeviceOnboardWizard";

/**
 * Admin tab: map DI1..DI8 on one physical counter node to different machines.
 * The AMeru edge pushes each channel to POST /api/iot/counter, which auto-fills
 * the mapped machine's "Update Production Quantity" and live dashboard count.
 *
 * Self-contained (own state, fetch, CRUD, modal + inline feedback) so it plugs
 * into AdminPanel with a single <IotDevicesTab /> line.
 */
const deviceMappings = (device) => (
  device?.mappings?.length
    ? device.mappings
    : [{ channel: device?.channel ?? 1, machine_code: device?.machine_code || "", rfid_tag_uid: "" }]
);

const mappingCount = (device, channel) => (
  device?.last_absolute_by_channel?.[String(channel)]
    ?? (channel === (device?.channel ?? 1) ? device?.last_absolute : 0)
    ?? 0
);

const IotDevicesTab = () => {
  const [devices, setDevices] = useState([]);
  const [machines, setMachines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState(null); // { text, type }
  const [showModal, setShowModal] = useState(false);
  const [showOnboard, setShowOnboard] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    device_id: "", mappings: [{ channel: "1", machine_code: "", rfid_tag_uid: "" }],
    token: "", status: "active",
  });

  const flash = (text, type = "success") => {
    setMsg({ text, type });
    setTimeout(() => setMsg(null), 3000);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [d, m] = await Promise.all([
        api.get("/iot/devices"),
        api.get("/machines").catch(() => api.get("/admin/machines")),
      ]);
      setDevices(d.data || []);
      setMachines(m.data || []);
    } catch (err) {
      flash(err.response?.data?.detail || "Failed to load IoT devices", "error");
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => {
    setEditing(null);
    setForm({
      device_id: "", mappings: [{ channel: "1", machine_code: "", rfid_tag_uid: "" }],
      token: "", status: "active",
    });
    setShowModal(true);
  };

  const openEdit = (d) => {
    setEditing(d.device_id);
    setForm({
      device_id: d.device_id,
      mappings: deviceMappings(d).map((m) => ({
        channel: String(m.channel), machine_code: m.machine_code,
        rfid_tag_uid: m.rfid_tag_uid || "",
      })),
      token: "", status: d.status || "active",
    });
    setShowModal(true);
  };

  const updateMapping = (index, key, value) => {
    setForm((f) => ({
      ...f,
      mappings: f.mappings.map((m, i) => (i === index ? { ...m, [key]: value } : m)),
    }));
  };

  const addMapping = () => {
    setForm((f) => {
      const used = new Set(f.mappings.map((m) => parseInt(m.channel)));
      const next = Array.from({ length: 8 }, (_, i) => i + 1).find((ch) => !used.has(ch));
      if (!next) return f;
      return {
        ...f,
        mappings: [...f.mappings, { channel: String(next), machine_code: "", rfid_tag_uid: "" }],
      };
    });
  };

  const removeMapping = (index) => {
    setForm((f) => ({ ...f, mappings: f.mappings.filter((_, i) => i !== index) }));
  };

  const save = async () => {
    const mappings = form.mappings.map((m) => ({
      channel: parseInt(m.channel),
      machine_code: (m.machine_code || "").trim(),
      rfid_tag_uid: (m.rfid_tag_uid || "").trim(),
    }));
    // Leaving machine blank is allowed — it means the DI channel is reserved
    // on the device but not yet mapped to any machine. Only Device ID and at
    // least one channel row are strictly required.
    if (!form.device_id || !mappings.length) {
      flash("Device ID and at least one channel row are required", "error");
      return;
    }
    try {
      const body = {
        device_id: form.device_id.trim(),
        mappings,
        // Legacy mirrors track the first mapping that actually has a machine.
        // If every channel is unmapped, mirror the first channel with a blank
        // machine — the backend's partial unique index tolerates that.
        machine_code: (mappings.find((m) => m.machine_code) || mappings[0]).machine_code,
        channel: mappings[0].channel || 1,
        status: form.status,
      };
      if (form.token) body.token = form.token;
      await api.post("/iot/devices", body);
      flash(`Device ${form.device_id} ${editing ? "updated" : "registered"}`);
      setShowModal(false);
      load();
    } catch (err) {
      flash(err.response?.data?.detail || "Save failed", "error");
    }
  };

  const remove = async (deviceId) => {
    if (!window.confirm(`Unlink counter device ${deviceId}?`)) return;
    try {
      await api.delete(`/iot/devices/${deviceId}`);
      flash(`Device ${deviceId} removed`);
      load();
    } catch (err) {
      flash(err.response?.data?.detail || "Delete failed", "error");
    }
  };

  return (
    <div>
      {msg && (
        <div className={`mb-3 px-4 py-2 rounded-lg text-sm font-bold ${
          msg.type === "error" ? "bg-red-100 text-red-700" : "bg-green-100 text-green-700"
        }`}>
          {msg.text}
        </div>
      )}

      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-extrabold text-slate-900 flex items-center gap-2">
            <Cpu size={18} /> IoT Counter Devices
          </h3>
          <p className="text-xs text-slate-500">
            Map DI1–DI8 on one physical node to up to eight machines. Each channel auto-fills its machine's Production Quantity.
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={load}
            className="inline-flex items-center gap-1 px-3 py-2 text-xs font-bold rounded-lg border border-slate-300 text-slate-600 hover:bg-slate-50">
            <RefreshCw size={14} /> Refresh
          </button>
          <button onClick={openAdd}
            className="inline-flex items-center gap-1 px-3 py-2 text-xs font-bold rounded-lg border border-slate-300 text-slate-600 hover:bg-slate-50">
            <Plus size={14} /> Register (manual)
          </button>
          <button onClick={() => setShowOnboard(true)}
            className="inline-flex items-center gap-1 px-3 py-2 text-xs font-bold rounded-lg bg-[#1A3560] text-white hover:bg-[#12294a]">
            <Wifi size={14} /> Onboard Device
          </button>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-widest text-slate-400">Device ID</th>
              <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-widest text-slate-400">DI Channel → Machine</th>
              <th className="text-right px-4 py-3 text-xs font-bold uppercase tracking-widest text-slate-400">Channel Counts</th>
              <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-widest text-slate-400">Last Seen</th>
              <th className="text-center px-4 py-3 text-xs font-bold uppercase tracking-widest text-slate-400">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} className="text-center py-6 text-slate-400">Loading…</td></tr>
            ) : devices.length === 0 ? (
              <tr><td colSpan={5} className="text-center py-6 text-slate-400">No counter devices registered</td></tr>
            ) : devices.map((d, i) => (
              <tr key={d.device_id} className={`border-t border-slate-100 ${i % 2 ? "bg-slate-50/50" : ""}`}>
                <td className="px-4 py-3 font-mono text-xs font-bold text-slate-900">{d.device_id}</td>
                <td className="px-4 py-3 text-xs text-slate-700">
                  {deviceMappings(d).map((m) => (
                    <div key={m.channel} className="mb-1 last:mb-0">
                      <span className="font-mono font-bold text-[#1A3560]">DI{m.channel}</span>
                      <span> → {m.machine_code || <em className="text-slate-400">(unassigned)</em>}</span>
                      {m.rfid_tag_uid && <span className="text-slate-400"> · RFID {m.rfid_tag_uid}</span>}
                    </div>
                  ))}
                </td>
                <td className="px-4 py-3 text-right text-xs tabular-nums">
                  {deviceMappings(d).map((m) => (
                    <div key={m.channel} className="mb-1 last:mb-0">
                      DI{m.channel}: {Number(mappingCount(d, m.channel)).toLocaleString()}
                    </div>
                  ))}
                </td>
                <td className="px-4 py-3 text-xs text-slate-500">
                  {d.last_seen_at ? new Date(d.last_seen_at).toLocaleString("en-IN") : "—"}
                </td>
                <td className="px-4 py-3 text-center whitespace-nowrap">
                  <button onClick={() => openEdit(d)}
                    className="text-xs font-bold text-[#1A3560] hover:underline mr-3">Edit</button>
                  <button onClick={() => remove(d.device_id)}
                    className="text-xs font-bold text-red-600 hover:underline inline-flex items-center gap-1">
                    <Trash2 size={12} /> Remove
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
          onClick={() => setShowModal(false)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-3xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-extrabold text-slate-900 mb-4">
              {editing ? "Edit" : "Register"} Counter Device
            </h3>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-bold text-slate-600 mb-1">Device ID</label>
                <input value={form.device_id} disabled={!!editing}
                  onChange={(e) => setForm((f) => ({ ...f, device_id: e.target.value }))}
                  placeholder="ws-s3eth-5f3aa32a"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm disabled:bg-slate-100" />
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-xs font-bold text-slate-600">DI Channel → Machine mappings</label>
                  <button onClick={addMapping} disabled={form.mappings.length >= 8}
                    className="text-xs font-bold text-[#1A3560] disabled:text-slate-300 inline-flex items-center gap-1">
                    <Plus size={13} /> Add channel
                  </button>
                </div>
                <div className="space-y-2">
                  {form.mappings.map((mapping, index) => (
                    <div key={`${index}-${mapping.channel}`} className="grid grid-cols-[64px_1fr_1fr_28px] gap-2 items-center">
                      <select value={mapping.channel}
                        onChange={(e) => updateMapping(index, "channel", e.target.value)}
                        className="px-2 py-2 border border-slate-300 rounded-lg text-sm">
                        {Array.from({ length: 8 }, (_, i) => i + 1).map((ch) => (
                          <option key={ch} value={ch}>DI{ch}</option>
                        ))}
                      </select>
                      <select value={mapping.machine_code}
                        data-testid={`iot-map-machine-${index}`}
                        onChange={(e) => updateMapping(index, "machine_code", e.target.value)}
                        className="px-2 py-2 border border-slate-300 rounded-lg text-sm">
                        <option value="">— Unassigned —</option>
                        {machines.map((m) => (
                          <option key={m.machine_code} value={m.machine_code}>{m.machine_code}</option>
                        ))}
                      </select>
                      <input value={mapping.rfid_tag_uid || ""}
                        onChange={(e) => updateMapping(index, "rfid_tag_uid", e.target.value)}
                        placeholder="RFID UID (future)"
                        className="px-2 py-2 border border-slate-300 rounded-lg text-xs font-mono" />
                      <button onClick={() => removeMapping(index)} disabled={form.mappings.length === 1}
                        className="text-red-500 disabled:text-slate-300" title="Remove mapping">
                        <Trash2 size={15} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-1 gap-3">
                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-1">Status</label>
                  <select value={form.status}
                    onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm">
                    <option value="active">active</option>
                    <option value="disabled">disabled</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-600 mb-1">
                  Ingest Token {editing && <span className="font-normal text-slate-400">(leave blank to keep)</span>}
                </label>
                <input value={form.token}
                  onChange={(e) => setForm((f) => ({ ...f, token: e.target.value }))}
                  placeholder="secret the edge sends as X-IoT-Token"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm" />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setShowModal(false)}
                className="px-4 py-2 text-sm font-bold rounded-lg border border-slate-300 text-slate-600">Cancel</button>
              <button onClick={save}
                className="px-4 py-2 text-sm font-bold rounded-lg bg-[#1A3560] text-white">
                {editing ? "Save" : "Register"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showOnboard && (
        <DeviceOnboardWizard
          machines={machines}
          onClose={() => setShowOnboard(false)}
          onDone={load}
        />
      )}
    </div>
  );
};

export default IotDevicesTab;
